import * as React from 'react';
import { MainState } from '../Core/reducers';
import { Dispatch, connect } from 'react-redux';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FilterReportsAction, ReportFormat, SortReportsAction, InitializeReportsAsyncAction, PageReportsAction, HideWidgetsAction, ToggleReportModalAction } from './ReportActions';
import Alert, { AlertCategoryEnum } from '../misc/components/Alert';
import Loading, { FontSize } from '../misc/components/loading';
import { Authorization } from '../misc/components/Authorization';
import { Link } from 'react-router-dom';
import { PermissionEnum } from '../misc/api/ApiPermissionEnum';
import { useCurrentStudyConfigurationContext } from '../Core/CurrentStudyConfigurationContext';
import { useWebPortalApiClient, useWebPortalLegacyClient } from '../misc/api/WebPortalTokenClient';
import { useSecurityContext } from '../Core/SecurityContext';
import { Column, Table } from '../Components/QueryTable';
import { MatchSortHeader } from '../Components/MatchSortHeader';
import { useQuery } from '../Components/Query';
import { DefinedReport } from 'webportalclient';
import ReportServerConnectionModal from './Administration/ReportServerConnectionModal';
import * as AdministrationActions from './Administration/AdministrationActions';
import { ReportAdministrationView } from './ReportsState';
import * as classNames from "classnames";
import { BaseReportsWizard } from './BaseReportsWizard/BaseReportsWizard';
import { ReportModal } from './Administration/ReportModal';
import { Permission } from '../misc/api/apiTypes';

export interface ReportsListTableProps {
    isConnectedToReportServer: boolean;
    isImportBaseReportsVisible: boolean;
    errorMessage: string;
    showReportModal: boolean;
    selectedStudyRootFolder?: string;
    selectedReportId?: string;
    dispatch?: Dispatch<MainState>;
}

export const ReportsListTable = (props: ReportsListTableProps) => {
    const [nameOptions, setNameOptions] = React.useState([]);
    const { study } = useCurrentStudyConfigurationContext();
    const client = useWebPortalApiClient();
    const { currentUserPermissions } = useSecurityContext();
    const { data, applyFilters, refresh } = useQuery();

    React.useEffect(() => {
        const getData = async () => {
            const showWidgets = currentUserPermissions.find(item => item.Id === PermissionEnum.ReportServerConnectionSettings);

            if (!showWidgets) {
                props.dispatch(HideWidgetsAction.create());
            }

            const nameOptions = await client.client.getDefinedReports('name', `studyIds/any(s : s eq ${study.id})`);
            setNameOptions(nameOptions.value.map(n => ({
                value: n.name,
                label: n.name,
            })));

        }

        getData();
    }, []);

    const handleDeleteReport = async (id: string) => {
        await client.client.deleteDefinedReport(id);
        refresh();
    }

    const toggleReportModal = () => {
        if (props.showReportModal)
            refresh();

        props.dispatch(ToggleReportModalAction.create());
    }

    const toggleReportModalById = (id: string) => {
        if (props.showReportModal)
            refresh();

        props.dispatch(ToggleReportModalAction.create(id));
    }

    const handleClearFilters = () => applyFilters([]);

    const onImportBaseReportsClick = () => {
        props.dispatch(AdministrationActions.ToggleImportBaseReportsModalVisibilityAction.create());
        refresh();
    }

    const onReportServerConnectionClick = () => {
        props.dispatch(AdministrationActions.ChangeViewAction.create(ReportAdministrationView.Credentials));
        props.dispatch(AdministrationActions.ToggleReportServerConnectionVisibilityAction.create());
    }

    const onSelectStudyFolderClick = () => {
        props.dispatch(AdministrationActions.ChangeViewAction.create(ReportAdministrationView.RootFolder));
        props.dispatch(AdministrationActions.ToggleReportServerConnectionVisibilityAction.create());
    }

    const renderHamburgerMenu = (row: DefinedReport, currentPermissions: Permission[]) => {
        const route = {
            pathname: `/reports/viewer/${encodeURIComponent(row.path)}`,
            state: row,
        }

        return (
            <div className="text-center">
                <div className="btn-group" role="group" data-automationid="hamburger-menu-button" data-report-name={row.name}>
                    <button type="button" className="btn btn-primary btn-xs dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                        <i className="fa fa-bars" aria-hidden="true"></i>
                    </button>
                    <ul className="dropdown-menu scrollable-menu" role="menu">
                        <li><Link to={route}>Run Report</Link></li>
                        <Authorization permissionId={PermissionEnum.ReportServerConnectionSettings} studyId={study.id} currentPermissions={currentPermissions}>
                            <li><a onClick={() => { toggleReportModalById(row.id) }}>Edit</a></li>
                        </Authorization>
                        <Authorization permissionId={PermissionEnum.ReportServerConnectionSettings} studyId={study.id} currentPermissions={currentPermissions}>
                            <li><a onClick={() => { handleDeleteReport(row.id) }}>Delete</a></li>
                        </Authorization>
                    </ul>
                </div>
            </div>
        );
    }

    const isConnectedTooltip = (<Tooltip id="descriptionTooltip" style={{ zIndex: 2500 }}>
        {props.isConnectedToReportServer ? "Connected" : "Cannot connect to Report Server"}
    </Tooltip>);

    const connectionStatusClasses = classNames("fas", "fa-circle", props.isConnectedToReportServer ? "text-success" : "text-danger");

    return (
        <>
            <div className="row">
                <div className="col-md-6">
                    <h2 className='text-primary'>Reports</h2>
                </div>
                <div className="col-md-6">
                    <div className="h2 pull-right">
                        <Authorization permissionId={PermissionEnum.ReportServerConnectionSettings} studyId={study.id} currentPermissions={currentUserPermissions}>
                            <OverlayTrigger placement="top" overlay={isConnectedTooltip}>
                                <button className="btn btn-primary" onClick={() => onReportServerConnectionClick()} data-automationid="report-server-connection-button">
                                    Report Server Connection
                                    &nbsp;
                                    <i className={connectionStatusClasses}></i>
                                </button>
                            </OverlayTrigger>&nbsp;
                            <button className="btn btn-primary" disabled={!props.isConnectedToReportServer} onClick={() => onSelectStudyFolderClick()} data-automationid="select-study-button">Select Study Folder</button>&nbsp;
                        </Authorization>
                        <Authorization permissionId={PermissionEnum.ImportBaseReports} studyId={study.id} currentPermissions={currentUserPermissions}>
                            <button className="btn btn-primary" onClick={() => onImportBaseReportsClick()}>Import Base Reports</button>&nbsp;
                        </Authorization>
                        <Authorization permissionId={PermissionEnum.ReportServerConnectionSettings} studyId={study.id} currentPermissions={currentUserPermissions}>
                            <button className="btn btn-primary" disabled={!props.isConnectedToReportServer || !props.selectedStudyRootFolder} onClick={toggleReportModal} data-automationid="add-report-button">Add Report</button>&nbsp;
                        </Authorization>
                        <button className="btn btn-primary" onClick={() => handleClearFilters()}>Clear All Filters</button>
                    </div>
                    <Authorization permissionId={PermissionEnum.ReportServerConnectionSettings} studyId={study.id} currentPermissions={currentUserPermissions}>
                        <ReportServerConnectionModal />
                    </Authorization>
                </div>
            </div>
            {
                props.isConnectedToReportServer == true ?
                <div className="row">
                    <div className="col-md-12">
                        <div className="panel panel-default">
                            <div className="panel-body">
                                <Alert category={AlertCategoryEnum.Danger} show={props.errorMessage !== null}>
                                    {props.errorMessage}
                                </Alert>
                                <Table data={data}>
                                    <Column field="name" header={<MatchSortHeader title="Report Name" field="name" options={nameOptions} />} formatter={n => n} />
                                    <Column field="" formatter={(row: DefinedReport) => renderHamburgerMenu(row, currentUserPermissions)} />
                                </Table>
                            </div>
                        </div>
                    </div>
                </div> : 
                <Alert show={true} category={AlertCategoryEnum.Danger}>There is no connection to the Report Server</Alert> 
            }
            <Authorization permissionId={PermissionEnum.ImportBaseReports} studyId={study.id} currentPermissions={currentUserPermissions}>
                {props.isImportBaseReportsVisible ? <BaseReportsWizard onClose={onImportBaseReportsClick} /> : null}
            </Authorization>
            {props.showReportModal ? <ReportModal definedReportId={props.selectedReportId} show={props.showReportModal} onCloseClick={toggleReportModal} /> : null}

        </>

    );
}

function mapStateToProps(state: MainState) {
    return {
        isImportBaseReportsVisible: state.reports.administration.isImportBaseReportsVisible,
        isConnectedToReportServer: state.reports.administration.isConnectedToReportServer,
        errorMessage: state.reports.listTable.errorMessage,
        showReportModal: state.reports.listTable.showReportModal,
        selectedStudyRootFolder: state.reports.administration.selectedStudyRootFolder,
        selectedReportId: state.reports.listTable.selectedReportId
    }
}

export default connect<ReportsListTableProps, any, void>(mapStateToProps)(ReportsListTable);
